import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CountUp from 'react-countup';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import config from '../../../../config';
import canSeeFinance from '../../../../utils/canSeeFinance';
import { NdoeStats, NdoeStatCard, NdoeEmpty } from '../../../../components/ndoe';

// Conversion sûre en nombre (les compteurs backend arrivent en number|string|undefined).
const num = (v) => (Number(v) || 0);

/**
 * StatLink — tuile KPI cliquable (navigation vers la vue filtrée de la période).
 * Défini au niveau module (identité stable) pour éviter de remonter les cartes
 * — et donc de relancer l'animation CountUp — à chaque refetch du parent.
 */
const StatLink = ({ onClick, label, value, gold = false }) => (
    <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
        style={{ cursor: 'pointer' }}
    >
        <NdoeStatCard label={label} value={value} gold={gold} />
    </div>
);

const RapportInformation = ({ start_date, end_date }) => {
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const user = useSelector((s) => s.user?.currentUser);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [commande, setCommande] = useState([]);
    const [livraison, setLivraison] = useState([]);
    const [client, setClient] = useState([]);
    const [mouvementEncours, setMouvementEncours] = useState([]);
    const [mouvementVente, setMouvementVente] = useState([]);
    const [dette, setDette] = useState([]);
    const [paiement, setPaiement] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get(`${DOMAIN}/api/depenses/caisseVenteCount?start_date=${start_date}&end_date=${end_date}`);
                setData(data);
            } catch (error) {
                console.log(error);
            } finally {
                setLoaded(true);
            }
        };
        fetchData();
    }, [DOMAIN, start_date, end_date]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get(`${DOMAIN}/api/depenses/caisseCommandeCount?start_date=${start_date}&end_date=${end_date}`);
                setCommande(data);
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();

        const timeoutId = setTimeout(fetchData, 4000);

        return () => clearTimeout(timeoutId);
    }, [DOMAIN, start_date, end_date]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get(`${DOMAIN}/api/depenses/caisseLivraisonCount?start_date=${start_date}&end_date=${end_date}`);
                setLivraison(data);
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();

        const timeoutId = setTimeout(fetchData, 4000);

        return () => clearTimeout(timeoutId);
    }, [DOMAIN, start_date, end_date]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get(`${DOMAIN}/api/depenses/caisseClientCount?start_date=${start_date}&end_date=${end_date}`);
                setClient(data);
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();

        const timeoutId = setTimeout(fetchData, 4000);

        return () => clearTimeout(timeoutId);
    }, [DOMAIN, start_date, end_date]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get(`${DOMAIN}/api/depenses/caisseMouvementCount?start_date=${start_date}&end_date=${end_date}`);
                setMouvementEncours(data[0]?.nbre_mouvement_encours);
                setMouvementVente(data[0]?.nbre_mouvement_vente);
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();

        const timeoutId = setTimeout(fetchData, 4000);

        return () => clearTimeout(timeoutId);
    }, [DOMAIN, start_date, end_date]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get(`${DOMAIN}/api/depenses/caisseDetteCount?start_date=${start_date}&end_date=${end_date}`);
                setDette(data[0]?.montant_total_restant);
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();
    }, [DOMAIN, start_date, end_date]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await axios.get(`${DOMAIN}/api/depenses/caissePaiementCount?start_date=${start_date}&end_date=${end_date}`);
                setPaiement(data[0]?.montant_total);
            } catch (error) {
                console.log(error);
            }
        };
        fetchData();
    }, [DOMAIN, start_date, end_date]);

    const periodSelected = Boolean(start_date && end_date);
    const hasAny = [
        data?.[0]?.total, commande?.[0]?.nbre, livraison?.[0]?.nbre_livraison,
        mouvementEncours, mouvementVente, dette, paiement, client,
    ].some((v) => num(v) > 0);

    // NdoeEmpty : une période explicitement choisie ne renvoie aucune activité.
    // (Ne remplace la grille que lorsque tous les compteurs valent 0 — sans période
    //  sélectionnée on garde l'affichage « tout l'historique » d'origine.)
    if (periodSelected && loaded && !hasAny) {
        return (
            <NdoeEmpty
                icon="🗓️"
                title="Aucune activité sur cette période"
                text="Aucune vente, commande ou livraison n'a été enregistrée pour l'intervalle sélectionné."
            />
        );
    }

    return (
        <NdoeStats>
            <StatLink
                onClick={() => navigate(`/ventes?start_date=${start_date}&end_date=${end_date}`)}
                label="Ventes"
                value={<CountUp end={num(data?.[0]?.total)} />}
            />
            <StatLink
                onClick={() => navigate(`/listeCommande?start_date=${start_date}&end_date=${end_date}`)}
                label="Commandes"
                value={<CountUp end={num(commande?.[0]?.nbre)} />}
            />
            <StatLink
                onClick={() => navigate(`/livraison_detail?start_date=${start_date}&end_date=${end_date}`)}
                label="Livraisons"
                value={<CountUp end={num(livraison?.[0]?.nbre_livraison)} />}
            />
            <StatLink
                onClick={() => navigate('/mouvement')}
                label="Mouvement en cours"
                value={<CountUp end={num(mouvementEncours)} />}
            />
            <StatLink
                onClick={() => navigate('/mouvement')}
                label="Mouvement vendu"
                value={<CountUp end={num(mouvementVente)} />}
            />
            {/* Garde finance (Lot 0) : montant à crédit réservé au rôle admin. */}
            {canSeeFinance(user) && (
                <StatLink
                    gold
                    onClick={() => navigate(`/dette?start_date=${start_date}&end_date=${end_date}`)}
                    label="Montant à crédit"
                    value={<><CountUp end={num(dette)} /> $</>}
                />
            )}
            {/* Garde finance (Lot 0) : remboursement réservé au rôle admin. */}
            {canSeeFinance(user) && (
                <StatLink
                    gold
                    onClick={() => navigate(`/paiement?start_date=${start_date}&end_date=${end_date}`)}
                    label="Remboursement"
                    value={<><CountUp end={num(paiement)} /> $</>}
                />
            )}
            <StatLink
                onClick={() => navigate(`/clients?start_date=${start_date}&end_date=${end_date}`)}
                label="Clients"
                value={<CountUp end={num(client)} />}
            />
        </NdoeStats>
    );
};

export default RapportInformation;
